<template>
    <div v-if="loaded" class="nevs-content">
        <div class="nevs-content-top-buttons">
            <NevsButton v-show="currentTab === 1" class="success" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>
            <span v-show="currentTab === 1">&nbsp;</span>
            <NevsButton class="error" @click="backClick"><i class="fa-solid fa-arrow-left"></i> {{
                    $LANG.Get('buttons.back')
                }}
            </NevsButton>
            <span v-if="mode === 'edit' && currentTab !== 1 && hasUnsavedChanges" class="unsaved-changes">
                {{ $LANG.Get('labels.unsavedChanges') }} {{ $LANG.Get('buttons.generalData') }}
            </span>
        </div>
        <NevsTabs class="company-tabs" :tabs="tabList" v-model="currentTab"></NevsTabs>
        <NevsTab :value="1" v-model="currentTab">
            <NevsCard class="company-form">
                <div class="company-form-grid">
                    <NevsTextField v-model="company.name" :error="companyValidation.name"
                                   :label="'*' + $LANG.Get('fields.name') + ':'"></NevsTextField>
                    <div class="company-form-oib">
                        <NevsTextField v-model="company.oib" :error="companyValidation.oib"
                                       :label="$LANG.Get('fields.oib') + ':'"></NevsTextField>
                        <NevsButton class="company-form-oib-button" :title="$LANG.Get('tooltips.getDataFromRegister')" @click="getDataFromRegister"><i class="fa-solid fa-magnifying-glass"></i></NevsButton>
                    </div>
                </div>

                <div class="company-form-foreign">
                    <NevsTextField v-if="company.foreign" :width="'300px'" v-model="company.oib_label"
                                   :error="companyValidation.oib_label"
                                   :label="$LANG.Get('fields.oibLabel') + ':'"></NevsTextField>
                    <NevsCheckbox v-model="company.foreign" :label="$LANG.Get('fields.foreignClient')"></NevsCheckbox>
                </div>

                <div class="company-form-flags">
                    <NevsCheckbox v-model="company.active" :label="$LANG.Get('fields.activeF')"></NevsCheckbox>
                </div>

                <div class="company-form-note">
                    <NevsTextArea v-model="company.note" :label="$LANG.Get('fields.note') + ':'"></NevsTextArea>
                </div>

                <div class="company-form-sections">
                    <div class="company-form-section">
                        <h2>{{ $LANG.Get('labels.emails') }}</h2>
                        <div v-if="companyValidation.emails" class="box-error nevs-text-error">{{ companyValidation.emails }}</div>
                        <div v-for="(email, key) in company.emails" :key="key" class="box-form">
                            <NevsTextField :error="email.validation" :label="'*' + $LANG.Get('fields.address') + ':'"
                                           v-model="email.address"></NevsTextField>
                            <NevsCheckbox :label="$LANG.Get('fields.officialF')" v-model="email.official"></NevsCheckbox>
                            <span @click="removeRecord(key, 'emails')" :title="$LANG.Get('tooltips.removeItem')"
                                  class="box-form-remove-button"><i class="fa-solid fa-trash"></i></span>
                        </div>
                        <NevsButton @click="addRecord('emails')"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.add') }}</NevsButton>
                    </div>
                    <div class="company-form-section">
                        <h2>{{ $LANG.Get('labels.phones') }}</h2>
                        <div v-if="companyValidation.phones" class="box-error nevs-text-error">{{ companyValidation.phones }}</div>
                        <div v-for="(phone, key) in company.phones" :key="key" class="box-form">
                            <NevsTextField :error="phone.validation.name" :label="'*' + $LANG.Get('fields.name') + ':'"
                                           v-model="phone.name"></NevsTextField>
                            <NevsTextField :error="phone.validation.number" :label="'*' + $LANG.Get('fields.number') + ':'"
                                           v-model="phone.number"></NevsTextField>
                            <br/><br/><br/>
                            <span @click="removeRecord(key, 'phones')" :title="$LANG.Get('tooltips.removeItem')"
                                  class="box-form-remove-button"><i class="fa-solid fa-trash"></i></span>
                        </div>
                        <NevsButton @click="addRecord('phones')"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.add') }}</NevsButton>
                    </div>
                    <div class="company-form-section">
                        <h2>{{ $LANG.Get('labels.addresses') }}</h2>
                        <div v-if="companyValidation.addresses" class="box-error nevs-text-error">{{
                                companyValidation.addresses
                            }}
                        </div>
                        <div v-for="(address, key) in company.addresses" :key="key" class="box-form">
                            <NevsTextArea :error="address.validation.address" :label="$LANG.Get('fields.streetAndHouseNo')"
                                          v-model="address.address"></NevsTextArea>
                            <NevsTextField :error="address.validation.postal_code" :label="$LANG.Get('fields.postalCode')"
                                           v-model="address.postal_code"></NevsTextField>
                            <NevsTextField :error="address.validation.city" :label="$LANG.Get('fields.city')"
                                           v-model="address.city"></NevsTextField>
                            <NevsTextField :error="address.validation.country" :label="$LANG.Get('fields.country')"
                                           v-model="address.country"></NevsTextField>
                            <NevsCheckbox :label="$LANG.Get('fields.officialF')" v-model="address.official"></NevsCheckbox>
                            <span @click="removeRecord(key, 'addresses')" :title="$LANG.Get('tooltips.removeItem')"
                                  class="box-form-remove-button"><i class="fa-solid fa-trash"></i></span>
                        </div>
                        <NevsButton @click="addRecord('addresses')"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.add') }}</NevsButton>
                    </div>
                </div>
            </NevsCard>
        </NevsTab>
        <NevsTab :value="2" v-model="currentTab">
            <NevsCard>
                <NevsSimpleGrid :fields="personFields" :records="companyPersons" :empty-text="$LANG.Get('labels.noPersons')"
                                @row-click="(record) => openPerson(record.id)">
                    <template #actions="{ record }">
                        <span :title="$LANG.Get('tooltips.openInNewWindow')" class="persons-table-button"
                              @click="openPerson(record.id, true)"><i class="fa-solid fa-up-right-from-square"></i></span>
                    </template>
                </NevsSimpleGrid>
            </NevsCard>
        </NevsTab>
        <NevsTab :value="3" v-model="currentTab">
            <NevsCard class="documents-card">
                <NevsMultiUpload @uploaded="documentsUploaded"></NevsMultiUpload>
                <div class="documents-list" @focusout="onDocumentsBlur">
                    <div v-if="company.documents.length === 0" class="documents-empty">
                        {{ $LANG.Get('labels.noDocuments') }}
                    </div>
                    <div v-for="(document, key) in company.documents" :key="key" class="document-row">
                        <i class="fa-solid fa-file document-row-icon"></i>
                        <NevsTextField class="document-row-name" width="260px" v-model="document.name"></NevsTextField>
                        <div class="document-row-file" :title="document.file.name">{{ document.file.name }}</div>
                        <div class="document-row-actions">
                            <span :title="$LANG.Get('tooltips.downloadFile')" class="document-row-button"
                                  @click="downloadDocument(document)"><i class="fa-solid fa-download"></i></span>
                            <span :title="$LANG.Get('tooltips.delete')"
                                  class="document-row-button document-row-button-delete"
                                  @click="deleteDocument(key)"><i class="fa-solid fa-trash"></i></span>
                        </div>
                    </div>
                </div>
            </NevsCard>
        </NevsTab>
        <NevsTab :value="4" v-model="currentTab">
            <NevsCard>
                <div class="invoices-tab-buttons">
                    <NevsButton class="success" @click="openNewInvoice()"><i class="fa-solid fa-file-invoice"></i>
                        {{ $LANG.Get('buttons.newInvoice') }}
                    </NevsButton>
                </div>
                <InvoicesGrid v-if="mode === 'edit' && invoicesTabOpened && canSeeInvoices" :endpoint="'invoices/by-company/' + company.id"></InvoicesGrid>
            </NevsCard>
        </NevsTab>
    </div>
</template>

<script>
import Company from '@/models/Company.js';
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsTextArea from "@/components/nevs/NevsTextArea.vue";
import InvoicesGrid from "@/components/general/InvoicesGrid.vue";
import NevsSimpleGrid from "@/components/nevs/NevsSimpleGrid.vue";
import NevsMultiUpload from "@/components/nevs/NevsMultiUpload.vue";
import NevsTabs from "@/components/nevs/NevsTabs.vue";
import NevsTab from "@/components/nevs/NevsTab.vue";

export default {
    name: "EntityCompany",
    components: {NevsMultiUpload, NevsSimpleGrid, InvoicesGrid, NevsTextArea, NevsTabs, NevsTab, NevsButton, NevsCheckbox, NevsTextField, NevsCard},
    data() {
        return {
            loaded: false,
            currentTab: 1,
            mode: this.$route.params.id === '0' ? 'add' : 'edit',
            pristineCompany: null,
            company: new Company({id: this.$route.params.id}),
            companyValidation: new Company({}, true),
            companyPersons: [],
            invoicesTabOpened: false,
            personFields: [
                {name: 'first_name', label: this.$LANG.Get('fields.firstName')},
                {name: 'last_name', label: this.$LANG.Get('fields.lastName')}
            ],
            templates: {
                emails: {
                    address: '',
                    documents: false,
                    official: false,
                    validation: ''
                },
                phones: {
                    number: '',
                    name: '',
                    validation: {
                        number: '',
                        name: ''
                    }
                },
                addresses: {
                    address: '',
                    postal_code: '',
                    city: '',
                    country: '',
                    official: false,
                    validation: {
                        address: '',
                        postal_code: '',
                        city: '',
                        country: ''
                    }
                }
            }
        }
    },
    watch: {
        'company.foreign'() {
            if (this.loaded) {
                this.company.oib_label = 'OIB';
            }
        },
        currentTab() {
            if (this.currentTab === 4) {
                this.invoicesTabOpened = true;
            }
        }
    },
    computed: {
        canSeeInvoices() {
            return this.$HELPERS.HasAnyPermission(this, ['ADMINISTRATOR', 'FINANCES']);
        },
        tabList() {
            return [
                {value: 1, label: this.$LANG.Get('buttons.generalData')},
                {value: 2, label: this.$LANG.Get('buttons.persons'), visible: this.mode === 'edit'},
                {value: 3, label: this.$LANG.Get('buttons.documents'), visible: this.mode === 'edit'},
                {value: 4, label: this.$LANG.Get('buttons.invoices'), visible: this.mode === 'edit' && this.canSeeInvoices}
            ];
        },
        hasUnsavedChanges() {
            if (this.pristineCompany === null) return false;
            // Documents are saved on their own tab (independently of the general "Save" button),
            // so they must not trigger the "unsaved changes" reminder on the first tab.
            let current = JSON.parse(JSON.stringify(this.company));
            let pristine = JSON.parse(this.pristineCompany);
            delete current.documents;
            delete pristine.documents;
            return JSON.stringify(current) !== JSON.stringify(pristine);
        }
    },
    methods: {
        // Opens the new-invoice screen for this company in a new browser tab, leaving this screen where it is;
        // the "Računi" tab's grid picks the new invoice up on its own through the reload-invoices cross-tab
        // event. The invoice is issued to a customer, not to a company, so the company's customer (created
        // together with the company) is resolved first and handed to EntityInvoice through the URL.
        openNewInvoice() {
            let vm = this;
            this.$API.APICall('get', 'companies/customer/' + this.company.id, {}, (data, success) => {
                if (success && data.customer_id !== null) {
                    window.open(vm.$router.resolve('/invoices/0/add?customer_id=' + data.customer_id).href);
                } else {
                    vm.$LOCAL_BUS.TriggerEvent('popup', {
                        text: vm.$LANG.Get('alerts.companyHasNoCustomer'),
                        type: 'alert'
                    });
                }
            });
        },
        getDataFromRegister() {
            let vm = this;
            this.$API.APICall('get', 'companies/from-registry', {oib: this.company.oib}, (data, success) => {
                if (success) {
                    if (data.success) {
                        let registryData = data.data;
                        vm.company.name = registryData.name;
                        if (vm.company.addresses.length == 0) {
                            vm.addRecord('addresses');
                            vm.company.addresses[0].official = true;
                        }
                        vm.company.addresses[0].address = registryData.address;
                        vm.company.addresses[0].city = registryData.city;
                        vm.company.addresses[0].postal_code = registryData.postal_code;
                        vm.company.addresses[0].country = registryData.country;
                    } else {
                        vm.$LOCAL_BUS.TriggerEvent('popup', {
                            text: vm.$LANG.Get('alerts.registryDataNotFound'),
                            type: 'alert'
                        });
                    }
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        removeRecord(key, type) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (response) {
                        vm.company[type].splice(key, 1);
                    }
                }
            });
        },
        addRecord(type) {
            this.company[type].push(JSON.parse(JSON.stringify(this.templates[type])))
        },
        loadCompanyPersons() {
            let vm = this;
            this.$API.APICall('get', 'persons/by-company/' + this.company.id, {}, (data, success) => {
                if (success) {
                    vm.companyPersons = data.persons;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        openPerson(id, newWindow = false) {
            if (newWindow) {
                let routeData = this.$router.resolve('/persons/' + id);
                window.open(routeData.href);
            } else {
                this.$router.push('/persons/' + id);
            }
        },
        documentsUploaded(files) {
            for (let file of files) {
                this.company.documents.push({
                    name: file.name,
                    file: file
                });
            }
            this.saveDocuments();
        },
        downloadDocument(document) {
            window.open(document.file.link);
        },
        deleteDocument(key) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.fileDeletionQuestion'), callback: (response) => {
                    if (response) {
                        vm.company.documents.splice(key, 1);
                        vm.saveDocuments();
                    }
                }
            });
        },
        documentsChanged() {
            if (this.pristineCompany === null) return true;
            let saved = JSON.parse(this.pristineCompany).documents || [];
            return JSON.stringify(saved) !== JSON.stringify(this.company.documents);
        },
        onDocumentsBlur() {
            if (this.documentsChanged()) {
                this.saveDocuments(false);
            }
        },
        saveDocuments(notify = true) {
            let vm = this;
            this.$API.APICall('put', 'companies/documents/' + this.company.id, {documents: this.company.documents}, (data, success) => {
                if (success) {
                    let pristine = JSON.parse(vm.pristineCompany);
                    pristine.documents = JSON.parse(JSON.stringify(vm.company.documents));
                    vm.pristineCompany = JSON.stringify(pristine);
                    if (notify) {
                        vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.recordSaved')});
                    }
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        backClick() {
            if (window.history.length === 1) {
                window.close();
            } else {
                this.$router.back();
            }
        },
        saveClick() {
            if (this.company.addresses.length === 0) {
                this.company.addresses.push(this.templates.addresses);
            }
            if (this.company.addresses.length === 1) {
                this.company.addresses[0].official = true;
            }
            if (this.company.emails.length === 1) {
                this.company.emails[0].official = true;
            }

            let valid = true;

            if (this.company.name === '') {
                valid = false;
                this.companyValidation.name = this.$LANG.Get('labels.mandatoryField');
            } else {
                this.companyValidation.name = '';
            }

            if (this.company.last_name === '') {
                valid = false;
                this.companyValidation.last_name = this.$LANG.Get('labels.mandatoryField');
            } else {
                this.companyValidation.last_name = '';
            }

            if (this.company.oib !== '') {
                if (!this.company.foreign) {
                    if (!this.$HELPERS.CheckOIB(this.company.oib)) {
                        valid = false;
                        this.companyValidation.oib = this.$LANG.Get('labels.invalidOIB');
                    } else {
                        this.companyValidation.oib = '';
                    }
                } else {
                    this.companyValidation.oib = '';
                }
            }

            if (this.company.emails.length !== 0) {
                for (let email of this.company.emails) {
                    if (email.address === '') {
                        valid = false;
                        email.validation = this.$LANG.Get('labels.mandatoryField');
                    } else {
                        if (!this.$HELPERS.CheckEmail(email.address)) {
                            valid = false;
                            email.validation = this.$LANG.Get('labels.invalidEmailAddress');
                        } else {
                            email.validation = '';
                        }
                    }
                }
                let officialEmails = 0;
                for (let email of this.company.emails) {
                    if (email.official) {
                        officialEmails++;
                    }
                }
                if (officialEmails !== 1) {
                    valid = false;
                    this.companyValidation.emails = this.$LANG.Get('labels.defaultEmailFail');
                } else {
                    this.companyValidation.emails = '';
                }
            } else {
                this.companyValidation.emails = '';
            }

            let official = 0;
            for (let address of this.company.addresses) {
                if (address.official) {
                    official++;
                }
            }
            if (official !== 1) {
                valid = false;
                this.companyValidation.addresses = this.$LANG.Get('labels.defaultAddressFail');
            } else {
                this.companyValidation.addresses = '';
            }

            for (let phone of this.company.phones) {
                if (phone.name === '') {
                    valid = false;
                    phone.validation.name = this.$LANG.Get('labels.mandatoryField');
                } else {
                    phone.validation.name = '';
                }
                if (phone.number === '') {
                    valid = false;
                    phone.validation.number = this.$LANG.Get('labels.mandatoryField');
                } else {
                    phone.validation.number = '';
                }
            }

            if (valid) {
                let vm = this;
                let requestMethod = this.mode === 'add' ? 'post' : 'put';
                let requestService = this.mode === 'add' ? 'companies' : 'companies/' + this.company.id;
                this.$API.APICall(requestMethod, requestService, this.company, (data, success) => {
                    if (success) {
                        vm.pristineCompany = JSON.stringify(vm.company);
                        vm.$CROSS_TAB_BUS.TriggerEvent('reload-companies', {});
                        vm.$LOCAL_BUS.TriggerEvent('notification', {text: this.$LANG.Get('alerts.recordSaved')});
                        if (vm.mode === 'add') vm.backClick();
                    } else {
                        if (data.error === 'oib_duplicate') {
                            vm.$LOCAL_BUS.TriggerEvent('popup', {
                                text: vm.$LANG.Get('alerts.oibDuplicate'),
                                type: 'alert'
                            });
                        } else {
                            vm.$HELPERS.HandleServerError(vm, data);
                        }
                    }
                })
            }
        }
    },
    mounted() {


        this.$store.commit('selectMenu', 'clients');
        this.$store.commit('selectSubMenu', 'companies');

        if (this.mode === 'add') {
            this.$store.commit('setBreadcrumbs', [
                {
                    label: this.$LANG.Get('modules.clients'),
                    link: null
                },
                {
                    label: this.$LANG.Get('modules.companies'),
                    link: '/companies'
                },
                {
                    label: this.$LANG.Get('modules.newCompany'),
                    link: null
                }
            ]);
            this.loaded = true;
        } else {
            let vm = this;
            this.$API.APICall('get', 'companies/' + this.company.id, {}, (data, success) => {
                if (success) {
                    if (data.company !== null) {
                        vm.company.Fill(data.company);
                        vm.pristineCompany = JSON.stringify(vm.company);
                        vm.loadCompanyPersons();
                        vm.$store.commit('setBreadcrumbs', [
                            {
                                label: vm.$LANG.Get('modules.clients'),
                                link: null
                            },
                            {
                                label: vm.$LANG.Get('modules.companies'),
                                link: '/companies'
                            },
                            {
                                label: vm.company.name,
                                link: null
                            }
                        ]);
                    } else {
                        vm.$HELPERS.HandleServerError(vm, data);
                    }
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
                vm.$nextTick(() => {
                    vm.loaded = true;
                });
            });
        }
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.invoices-tab-buttons {
    margin-bottom: 12px;
}

.unsaved-changes {
    margin-left: 10px;
    color: $danger;
    font-size: 13px;
}

.company-tabs {
    margin-top: 18px;
    margin-bottom: 6px;
}

.form {
    max-width: 300px;
    margin-bottom: 10px;
    padding: 15px;
}

.form-float {
    width: 300px;
    float: left;
    margin-right: 10px;
    margin-bottom: 10px;
    padding: 15px;
}

.form-float h2 {
    margin: 0 0 5px;
}

.company-form {
    max-width: 1080px;
    padding: 16px;
}

.company-form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 4px 12px;
    align-items: start;
}

.company-form-oib {
    display: flex;
    align-items: flex-end;
    gap: 8px;
}

/* Lift the lookup button so it is vertically centered on the OIB input box, not the whole field:
   the field adds 5px below the input and the input has 3px more padding than the button (13px vs 10px). */
.company-form-oib-button {
    margin-bottom: 8px;
}

.company-form-foreign {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 8px 5px 0;
}

.company-form-flags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 28px;
    padding: 10px 5px;
}

.company-form-note {
    padding: 0 5px;
}

.company-form-sections {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 22px;
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid $sand-300;
}

.company-form-section h2 {
    margin: 0 0 8px;
    font-size: 1.05em;
}

.box-form {
    border: 2px solid $sand-400;
    padding: 5px;
    border-radius: 5px;
    margin-bottom: 5px;
    position: relative;
}

.box-error {
    font-size: 12px;
    margin-bottom: 5px;
    text-align: justify;
}

.box-form-remove-button {
    cursor: pointer;
    position: absolute;
    right: 5px;
    bottom: 5px;
}

.persons-table-button {
    cursor: pointer;
}

.documents-card {
    max-width: 600px;
}

.documents-list {
    margin-top: 20px;
}

.documents-empty {
    color: $ink-muted;
    font-style: italic;
}

.document-row {
    display: flex;
    align-items: center;
    gap: 12px;
    border: 2px solid $sand-400;
    border-radius: 5px;
    padding: 8px 12px;
    margin-bottom: 8px;
    background: $white;
}

.document-row-icon {
    font-size: 22px;
    color: $orange-icon;
    flex-shrink: 0;
}

.document-row-name {
    width: 260px;
    flex-shrink: 0;
}

.document-row-file {
    flex: 1;
    min-width: 0;
    color: $ink-muted;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.document-row-actions {
    display: flex;
    gap: 12px;
    flex-shrink: 0;
}

.document-row-button {
    cursor: pointer;
    color: $ink-muted;
}

.document-row-button:hover {
    color: $ink-strong;
}

.document-row-button-delete:hover {
    color: $danger;
}
</style>